import { useCallback, useEffect, useState } from 'react'
import Icon from './icons.jsx'
import { onboardWhatsappMeta, disconnectWhatsappMeta } from '../db/api.js'
import { launchEmbeddedSignup, isMetaSignupConfigured } from '../lib/metaSignup.js'

const CLOSE_MS = 200

/**
 * Modal de integração WhatsApp Oficial (Meta Cloud API + CoEx) por cliente.
 *
 * Espelha WhatsappIntegrationModal (Evolution) no visual e na experiência.
 * Diferenças do provider Meta:
 *  - Conexão via Embedded Signup CoEx (popup do Facebook), não QR.
 *  - Conta conectada exibe número/nome verificado vindos da Meta.
 *  - Desconectar revoga a conta (status='revoked'), sem apagar histórico.
 *
 * Props:
 *  - open     : controla montagem.
 *  - client   : cliente alvo ({ id, name }).
 *  - account  : conta Meta já vinculada a este cliente (ou null).
 *  - onClose  : callback ao fechar (o pai recarrega o status).
 */
export default function MetaIntegrationModal({ open, client, account, onClose }) {
  const [mounted, setMounted] = useState(open)
  const [closing, setClosing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  // Conta local: começa com a recebida via prop; atualiza após conectar/desconectar.
  const [acc, setAcc] = useState(account || null)

  const clientId = client?.id
  const isConnected = acc && acc.status === 'active'

  const startClose = useCallback(() => {
    setClosing(true)
    setTimeout(() => {
      setClosing(false)
      setMounted(false)
      onClose?.()
    }, CLOSE_MS)
  }, [onClose])

  // Sincroniza montagem e reseta estado a cada abertura.
  useEffect(() => {
    if (open) {
      setMounted(true)
      setClosing(false)
      setBusy(false)
      setError('')
      setSuccess('')
      setAcc(account || null)
    }
  }, [open, account])

  // Esc fecha.
  useEffect(() => {
    if (!mounted) return
    const onKey = (e) => e.key === 'Escape' && startClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mounted, startClose])

  async function handleConnect() {
    if (!clientId) {
      setError('Cliente inválido.')
      return
    }
    if (!isMetaSignupConfigured()) {
      setError('Integração Meta não configurada (VITE_META_APP_ID / VITE_META_CONFIG_ID).')
      return
    }
    setError('')
    setSuccess('')
    setBusy(true)
    try {
      const { code, wabaId, phoneNumberId } = await launchEmbeddedSignup()
      const result = await onboardWhatsappMeta({
        clientId,
        code,
        wabaId,
        phoneNumberId,
      })
      setAcc({ ...result, status: 'active' })
      setSuccess('Número conectado com sucesso.')
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  async function handleDisconnect() {
    if (!acc?.id) return
    if (!window.confirm('Desconectar a conta Meta deste cliente?')) return
    setError('')
    setSuccess('')
    setBusy(true)
    try {
      await disconnectWhatsappMeta(acc.id)
      setAcc((prev) => (prev ? { ...prev, status: 'revoked' } : prev))
      setSuccess('Conta desconectada.')
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  if (!mounted) return null

  return (
    <div
      className={`sup-overlay${closing ? ' is-closing' : ''}`}
      onMouseDown={(e) => e.target === e.currentTarget && startClose()}
    >
      <div className="sup-modal" role="dialog" aria-modal="true">
        <button className="sup-close" onClick={startClose} aria-label="Fechar">
          <Icon n="x" s={16} />
        </button>

        <div className="sup-head">
          <div className="sup-title">WhatsApp API Oficial (Meta)</div>
          <div className="sup-subtitle">
            {client?.name ? `Cliente: ${client.name}` : 'Conecte a conta oficial da Meta.'}
          </div>
        </div>

        {error && <div className="auth-error" style={{ marginBottom: 12 }}>{error}</div>}
        {success && (
          <div
            className="auth-error"
            style={{ marginBottom: 12, background: '#f0fdf4', color: '#16a34a', borderColor: '#bbf7d0' }}
          >
            {success}
          </div>
        )}

        {isConnected ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ textAlign: 'center', padding: '4px 0' }}>
              <div
                style={{
                  width: 56, height: 56, borderRadius: '50%', margin: '0 auto 12px',
                  background: '#f0fdf4', color: '#16a34a', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700,
                }}
              >
                ✓
              </div>
              <p style={{ margin: 0, fontWeight: 700, color: '#16a34a' }}>
                Conta Meta conectada.
              </p>
            </div>

            {/* Informações da conta conectada */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                ['Número', acc.display_phone_number || acc.phone_number_id],
                ['Nome verificado', acc.verified_name || '—'],
                ['WABA', acc.waba_id || '—'],
              ].map(([l, v], i) => (
                <div
                  key={i}
                  style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--soft)', fontSize: 14 }}
                >
                  <span style={{ color: 'var(--sec)' }}>{l}</span>
                  <span style={{ fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>

            <button
              className="btn btn-outline btn-full"
              onClick={handleDisconnect}
              disabled={busy}
            >
              {busy ? 'Desconectando…' : 'Desconectar'}
            </button>
          </div>
        ) : (
          <>
            <p className="sup-subtitle" style={{ marginBottom: 16 }}>
              Conecte o número que já roda no WhatsApp Business App. Ele continua
              ativo no celular e passa a enviar/receber também pela API oficial da
              Meta. Uma janela do Facebook abrirá para autorizar.
            </p>
            <button className="btn btn-black btn-full" onClick={handleConnect} disabled={busy}>
              {busy ? 'Conectando…' : 'Conectar via Meta'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
